import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";

const UserStateContext = createContext(null);

export const useUserState = () => {
  const ctx = useContext(UserStateContext);
  if (!ctx) throw new Error("useUserState must be used within UserStateProvider");
  return ctx;
};

/**
 * UserStateProvider — v1.0.0 Gate 1 (Section 1.2)
 *
 * Fetches the 6-state user birthday state from the server.
 * Provides: state, days_until_birthday, greeting, disclosure_level.
 */
export function UserStateProvider({ children }) {
  const { user } = useAuth();
  const [userState, setUserState] = useState({
    state: "standard",
    days_until_birthday: null,
    days_after_birthday: null,
    greeting: "",
    copy_bank_key: "",
    disclosure_level: 1,
    display_name: "",
    loading: true,
  });

  const fetchState = useCallback(async () => {
    if (!user) {
      setUserState((prev) => ({ ...prev, loading: false }));
      return;
    }
    try {
      const res = await axios.get(`${API}/user-state/me`);
      setUserState({ ...res.data, loading: false });
    } catch {
      setUserState((prev) => ({ ...prev, loading: false }));
    }
  }, [user]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  const markActivity = useCallback(async () => {
    try {
      await axios.post(`${API}/user-state/mark-activity`);
      fetchState();
    } catch {}
  }, [fetchState]);

  const checkUnlock = useCallback(
    async (action) => {
      try {
        const res = await axios.post(`${API}/user-state/unlock-level`, { action });
        if (res.data?.upgraded) {
          setUserState((prev) => ({
            ...prev,
            disclosure_level: res.data.level,
          }));
        }
        return res.data;
      } catch {
        return { upgraded: false };
      }
    },
    []
  );

  return (
    <UserStateContext.Provider value={{ ...userState, refetch: fetchState, markActivity, checkUnlock }}>
      {children}
    </UserStateContext.Provider>
  );
}
